<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as DispatchAssignDriverDialogFormInitialData,
  ValidData as DispatchAssignDriverDialogFormValidData,
} from '@/components/dialogForms/DispatchAssignDriverDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import JobsBasicList from '@/components/basicLists/JobsBasicList.vue';
import KBtn from '@/components/btns/KBtn.vue';
import DispatchAssignDriverDialogForm from '@/components/dialogForms/DispatchAssignDriverDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  jobs?: { jobId: number; jobDate: string }[];
  action?: 'allocate' | 'preallocate';
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  jobs: () => [],
  action: 'allocate',
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: DispatchAssignDriverDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: DispatchAssignDriverDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<DispatchAssignDriverDialogFormInitialData>(() => {
  return {
    driver: undefined,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.dispatchAssignDriverDialog"
    data-testid="dispatchAssignDriverDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        <template v-if="props.action === 'preallocate'">
          Pre-allocate jobs to a driver
        </template>
        <template v-else> Assign jobs to a driver </template>
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <JobsBasicList :jobs="props.jobs" class="mt-0 mb-6" />
      <DispatchAssignDriverDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Confirm
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchAssignDriverDialog {
  width: variables.$reportGenerateDialogWidth;
}
  .main {
  }
  .text {
    margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
